import type { ToggleButtonGroupProps } from '@mui/material'
import { FormControl, FormControlLabel, FormHelperText, ToggleButtonGroup } from '@mui/material'
import { forwardRef } from 'react'
import type { ControllerProps, FieldPath, FieldValues } from 'react-hook-form'
import { Controller, useFormContext } from 'react-hook-form'

export type ToggleButtonGroupControllerProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = Omit<ToggleButtonGroupProps, 'name'> & {
  name: TName
  label?: string
  showError?: boolean
  controllerProps?: Omit<ControllerProps<TFieldValues, TName>, 'render' | 'name'>
}

function ToggleButtonGroupField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: Readonly<Omit<ToggleButtonGroupControllerProps<TFieldValues, TName>, 'controllerProps'>>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  if (!props.label) {
    return <ToggleButtonGroup value={props.value} {...props} ref={ref} />
  }

  return (
    <FormControlLabel ref={ref} control={<ToggleButtonGroup value={props.value} {...props} />} label={props.label} />
  )
}

const ToggleButtonGroupFieldWithRef = forwardRef(ToggleButtonGroupField)

function ToggleButtonGroupController<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  label,
  name,
  showError = true,
  controllerProps,
  ...fieldProps
}: ToggleButtonGroupControllerProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>()

  return (
    <Controller
      control={control}
      name={name}
      {...controllerProps}
      render={({ field: { ref, ...field }, fieldState: { error, invalid } }) =>
        showError ? (
          <FormControl error={invalid}>
            <ToggleButtonGroupFieldWithRef
              {...field}
              value={field.value}
              {...fieldProps}
              ref={ref as React.ForwardedRef<HTMLDivElement>}
              label={label}
            />

            {invalid && showError && <FormHelperText>{error?.message}</FormHelperText>}
          </FormControl>
        ) : (
          <FormControlLabel
            control={<ToggleButtonGroup {...field} value={field.value} {...fieldProps} />}
            label={label}
          />
        )
      }
    />
  )
}

export default ToggleButtonGroupController
